import { useState } from "react";
import { useFormContext, Controller } from "react-hook-form";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import _ from "lodash";

const MultiSelectDatePicker = ({
    label,
    name,
    required,
    placeholder = "Select Dates",
    className = "",
    showTimeSelect,
    showTimeSelectOnly,
    dateFormat,
    shouldUnregister,
    excludeTimes,
    filterTime,
    maxDate,
    minDate,
    onChangeHandler,
    readOnly,
    padding = "px-4 py-2",
}) => {
    const {
        formState: { errors },
        control,
    } = useFormContext();

    const [isOpen, setIsOpen] = useState(false);
    const [inputRef, setInputRef] = useState(null);

    const getDateFormat = () => {
        let format = 'dd/MM/yyyy';
        if (showTimeSelect) {
            if (showTimeSelectOnly) {
                format = 'HH:mm a';
            } else if (dateFormat) {
                format = dateFormat;
            } else {
                format = 'dd/MM/yyyy HH:mm';
            }
        }
        return format;
    };

    return (
        <div className={`${padding} ${className}`}>
            {label && (
                <label>
                    <span className='text-scogo99 font-normal text-font09 md:text-font11 labelFotnSize'>
                        {label} {required && <span className='text-scogoorange'>*</span>}{' '}
                    </span>
                </label>
            )}
            <div className="relative">
                <Controller
                    key={name}
                    control={control}
                    name={name}
                    rules={{ required: { value: required, message: "Required" } }}
                    shouldUnregister={shouldUnregister}
                    render={({ field: { onChange, value, name } }) => {
                        const maxVisibleDates = 2;
                        const visibleDates = (value || []).slice(0, maxVisibleDates);
                        const remainingCount = (value?.length || 0) - maxVisibleDates;

                        const handleDateChange = (date) => {
                            if (!date || readOnly) return;

                            let updatedDates = [...(value || [])];

                            // Check if the date already exists in the array
                            const existingIndex = updatedDates.findIndex(
                                (d) => d.setHours(0, 0, 0, 0) === date.setHours(0, 0, 0, 0)
                            );

                            if (existingIndex !== -1) {
                                // Remove the date if it already exists
                                updatedDates.splice(existingIndex, 1);
                            } else {
                                // Add the date if it doesn't exist
                                updatedDates.push(date);
                            }

                            onChange(updatedDates);
                            if (onChangeHandler) {
                                onChangeHandler(updatedDates);
                            }
                        };

                        return (
                            <>
                                <div
                                    ref={setInputRef}
                                    className={`border ${readOnly ? 'border-scogogray bg-scogof2' : 'border-scogoccc focus:border-scogoprimary bg-white'
                                        } w-full pl-10 rounded-6px text-scogo99 font-normal text-font09 md:text-font11 py-2`}
                                    onClick={() => !readOnly && setIsOpen(true)}
                                    title={(value || []).map((d) => d.toLocaleDateString()).join(", ")}
                                >
                                    {value && value.length > 0
                                        ? visibleDates.map((date) => date.toLocaleDateString()).join(", ") +
                                        (remainingCount > 0 ? ` +${remainingCount} more` : "")
                                        : placeholder}
                                </div>

                                <span className='material-icons-outlined text-scogoorange absolute left-2 top-2 font-normal text-font16 inputFontSize'>event</span>

                                {isOpen && inputRef && !readOnly && (
                                    <div
                                        className="fixed z-[1001] shadow-lg"
                                        style={{
                                            position: 'fixed',
                                            top: 'auto',
                                            bottom: 'auto',
                                            left: `${inputRef.getBoundingClientRect().left + window.scrollX}px`,
                                            maxHeight: '80vh',
                                            overflowY: 'auto',
                                            zIndex: 999999999999999,
                                        }}
                                        ref={(node) => {
                                            if (node) {
                                                const rect = node.getBoundingClientRect();
                                                const viewportHeight = window.innerHeight;
                                                const inputRect = inputRef.getBoundingClientRect();

                                                // Check if there's enough space below the input
                                                if (inputRect.bottom + rect.height > viewportHeight) {
                                                    // Not enough space below, try to position above
                                                    if (inputRect.top > rect.height) {
                                                        // Enough space above
                                                        node.style.top = 'auto';
                                                        node.style.bottom = `${window.innerHeight - inputRect.top + window.scrollY}px`;
                                                    } else {
                                                        // Not enough space above either, position at the center of the viewport
                                                        node.style.top = `${Math.max(window.scrollY + 20, window.scrollY + (viewportHeight - rect.height) / 2)}px`;
                                                    }
                                                } else {
                                                    // Enough space below
                                                    node.style.top = `${inputRect.bottom + window.scrollY}px`;
                                                    node.style.bottom = 'auto';
                                                }
                                            }
                                        }}
                                    >
                                        <div className="bg-white rounded-lg shadow-lg p-4" onClick={(e) => e.stopPropagation()}>
                                            <ReactDatePicker
                                                inline
                                                multiple
                                                selected={null}
                                                onChange={handleDateChange}
                                                highlightDates={value || []}
                                                selectsMultiple
                                                showTimeSelect={showTimeSelect}
                                                showTimeSelectOnly={showTimeSelectOnly}
                                                dateFormat={getDateFormat()}
                                                excludeTimes={excludeTimes}
                                                filterTime={filterTime}
                                                maxDate={maxDate || undefined}
                                                minDate={minDate || undefined}
                                                disabled={readOnly}
                                                showMonthDropdown
                                                showYearDropdown
                                                scrollableYearDropdown
                                                yearDropdownItemNumber={50}
                                                name={name}
                                            />
                                            <div className="flex justify-end mt-2">
                                                <button
                                                    className="bg-scogoprimary text-white px-4 py-2 rounded"
                                                    onClick={() => setIsOpen(false)}
                                                >
                                                    Done
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Overlay to capture clicks outside the datepicker */}
                                {isOpen && !readOnly && (
                                    <div
                                        className="fixed inset-0 z-[1000]"
                                        onClick={() => setIsOpen(false)}
                                    />
                                )}
                            </>
                        );
                    }}
                />
                {_.get(errors, name) && <span className='text-scogoclosed text-font08 font-normal'>{_.get(errors, name).message}</span>}
            </div>
        </div>
    );
};

export default MultiSelectDatePicker;
